import { useId, useMemo, useState, type ReactNode } from 'react';
import {
  CalendarCheck,
  CalendarX2,
  ChevronDown,
  ChevronRight,
  Clock,
  HeartPulse,
  MessagesSquare,
  Pill,
  Plane,
  Search,
  SearchX,
  Stethoscope,
  Syringe,
  TestTubeDiagonal,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { PublicCondition, PublicService } from '@rx-manager/shared';
import { useI18n, type MessageKey } from '../lib/i18n';
import { ChoiceCard, Chip, IconTile, cn } from './ui';

/**
 * The first step: choose what to book.
 *
 * A flat list worked for a pharmacy offering a flu shot. It stopped working at a
 * full menu — nine buttons of equal weight, where "Travel health consultation"
 * sat between two vaccines and the patient had to read every one. Patients
 * arrive knowing the *kind* of thing they need, so services are grouped by
 * category into sections that open and close, with a search box above for the
 * patient who already knows the name.
 *
 * The pharmacy's website renders its `/book` page with the same grouping, the
 * same order and the same icons (RxManager_BE `renderer/booking-catalog.ts`),
 * so a patient who starts on the website sees the same layout here.
 */

/** The order a patient scans for them: the common walk-in reasons first. */
const CATEGORY_ORDER = [
  'vaccination',
  'travel',
  'minor_ailment',
  'medication_review',
  'testing',
  'consult',
] as const;

interface CategoryInfo {
  label: MessageKey;
  icon: LucideIcon;
}

const CATEGORY: Record<string, CategoryInfo> = {
  vaccination: { label: 'service.category.vaccination', icon: Syringe },
  travel: { label: 'service.category.travel', icon: Plane },
  minor_ailment: { label: 'service.category.minor_ailment', icon: Stethoscope },
  medication_review: { label: 'service.category.medication_review', icon: Pill },
  testing: { label: 'service.category.testing', icon: TestTubeDiagonal },
  consult: { label: 'service.category.consult', icon: MessagesSquare },
};

const OTHER: CategoryInfo = { label: 'service.category.other', icon: CalendarCheck };

/** A service's icon: its category's. */
export function categoryIcon(category: string): LucideIcon {
  return (CATEGORY[category] ?? OTHER).icon;
}

export const METHOD_LABEL: Record<string, MessageKey> = {
  in_person: 'service.methodInPerson',
  phone: 'service.methodPhone',
  virtual: 'service.methodVirtual',
  walk_in: 'service.methodWalkIn',
};

export interface ServiceGroup {
  key: string;
  info: CategoryInfo;
  services: PublicService[];
}

/** Known categories in `CATEGORY_ORDER`, then anything else as one group. */
export function groupServices(services: PublicService[]): ServiceGroup[] {
  const known = new Map<string, PublicService[]>();
  const other: PublicService[] = [];
  for (const service of services) {
    if (CATEGORY[service.category]) {
      known.set(service.category, [...(known.get(service.category) ?? []), service]);
    } else {
      other.push(service);
    }
  }
  const groups: ServiceGroup[] = CATEGORY_ORDER.filter((key) => known.has(key)).map(
    (key) => ({ key, info: CATEGORY[key]!, services: known.get(key)! }),
  );
  if (other.length > 0) groups.push({ key: 'other', info: OTHER, services: other });
  return groups;
}

/**
 * A short menu opens every section — hiding three services behind a click is
 * all cost. A long one opens only the first, so the step starts as a short list
 * of headings rather than a wall of tiles.
 */
const OPEN_ALL_UP_TO = 6;

/** A condition's name and line in the page's language, English when French is missing. */
export function conditionText(condition: PublicCondition, locale: string): { name: string; blurb: string | null } {
  const fr = locale === 'fr';
  return {
    name: (fr && condition.name.fr) || condition.name.en,
    blurb: (fr && condition.blurb.fr) || condition.blurb.en,
  };
}

/** Inside a card that clips its corners, the ring is drawn inside the control. */
const INSET_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring';

export function ServicePicker({
  services,
  onChoose,
  titleSuffix,
  conditions = [],
  onChooseCondition,
}: {
  services: PublicService[];
  onChoose: (service: PublicService) => void;
  /** Rendered after each title — the "(in English)" marker on the French page. */
  titleSuffix?: ReactNode;
  /** Track BCW (BCW-D2) — "What do you need help with?", above the services. */
  conditions?: PublicCondition[];
  onChooseCondition?: (condition: PublicCondition) => void;
}) {
  const { t, locale } = useI18n();
  const searchId = useId();
  const groups = useMemo(() => groupServices(services), [services]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Set<string>>(() =>
    services.length <= OPEN_ALL_UP_TO || groups.length === 1
      ? new Set(groups.map((group) => group.key))
      : new Set(groups.slice(0, 1).map((group) => group.key)),
  );

  if (services.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border px-4 py-10 text-center">
        <IconTile icon={CalendarX2} tone="muted" size="lg" />
        <p className="max-w-sm text-base text-muted-foreground">{t('service.none')}</p>
      </div>
    );
  }

  const needle = query.trim().toLowerCase();
  const visibleConditions = onChooseCondition
    ? conditions.filter((condition) => {
        if (!needle) return true;
        const text = conditionText(condition, locale);
        return [text.name, text.blurb ?? '', condition.name.en].join(' ').toLowerCase().includes(needle);
      })
    : [];
  const visible = groups
    .map((group) => ({
      ...group,
      services: needle
        ? group.services.filter((service) =>
            [service.title, service.description ?? '', t(group.info.label)]
              .join(' ')
              .toLowerCase()
              .includes(needle),
          )
        : group.services,
    }))
    .filter((group) => group.services.length > 0);

  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="space-y-5">
      <div className="relative">
        <label htmlFor={searchId} className="sr-only">
          {t('service.search')}
        </label>
        <Search
          className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          id={searchId}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('service.searchPlaceholder')}
          autoComplete="off"
          enterKeyHint="search"
          className={cn(
            'h-14 w-full rounded-full border border-input bg-card pl-14 pr-14 text-base text-foreground shadow-sm',
            'placeholder:text-muted-foreground hover:border-foreground/50',
            'focus-visible:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
            '[&::-webkit-search-cancel-button]:hidden',
          )}
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute right-2.5 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full text-muted-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            aria-label={t('service.searchClear')}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        )}
      </div>

      {visibleConditions.length > 0 && (
        <section
          aria-labelledby={`${searchId}-conditions`}
          className="rounded-2xl bg-accent/60 p-4 ring-1 ring-inset ring-primary/10 sm:p-5"
        >
          <div className="flex items-start gap-3">
            <IconTile icon={HeartPulse} className="bg-card" />
            <div className="min-w-0">
              <h2 id={`${searchId}-conditions`} className="text-lg font-semibold text-heading">
                {t('condition.heading')}
              </h2>
              <p className="mt-0.5 text-sm text-muted-foreground">{t('condition.help')}</p>
            </div>
          </div>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {visibleConditions.map((condition) => {
              const text = conditionText(condition, locale);
              return (
                <li key={condition.id}>
                  <ChoiceCard
                    onClick={() => onChooseCondition?.(condition)}
                    title={text.name}
                    detail={text.blurb}
                  />
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {visible.length === 0 && visibleConditions.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border px-4 py-10 text-center">
          <IconTile icon={SearchX} tone="muted" size="lg" />
          <p role="status" className="max-w-sm text-base text-muted-foreground">
            {t('service.noMatch', { query: query.trim() })}
          </p>
        </div>
      ) : (
        visible.map((group) => {
          // While searching, every section with a match is open: the patient
          // asked to see matches, not to hunt for which heading hides them.
          const expanded = needle !== '' || open.has(group.key);
          const panelId = `${searchId}-${group.key}`;
          const Icon = group.info.icon;
          return (
            <section
              key={group.key}
              className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
            >
              <h2>
                <button
                  type="button"
                  onClick={() => toggle(group.key)}
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  className={cn(
                    'flex min-h-[4.5rem] w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/50 sm:gap-4 sm:px-5',
                    INSET_RING,
                  )}
                >
                  <IconTile icon={Icon} />
                  <span className="min-w-0 flex-1 text-lg font-semibold text-heading">
                    {t(group.info.label)}
                  </span>
                  <Chip className="hidden sm:inline-flex">
                    {group.services.length === 1
                      ? t('service.countOne')
                      : t('service.countMany', { count: group.services.length })}
                  </Chip>
                  <span
                    aria-hidden="true"
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground"
                  >
                    <ChevronDown
                      className={cn(
                        'h-5 w-5 transition-transform motion-reduce:transition-none',
                        expanded && 'rotate-180',
                      )}
                    />
                  </span>
                </button>
              </h2>
              {/* The class as well as the attribute, so a display utility added here later
                  cannot beat the browser's own [hidden] rule. */}
              <ul
                id={panelId}
                hidden={!expanded}
                className={cn('divide-y divide-border border-t border-border', !expanded && 'hidden')}
              >
                {group.services.map((service) => {
                  const rowId = `${searchId}-${service.id}`;
                  return (
                    <li key={service.id}>
                      <button
                        type="button"
                        onClick={() => onChoose(service)}
                        // The title and its facts name the button; the description describes it,
                        // so a screen reader hears "Flu shot, 15 minutes, In person" first.
                        aria-labelledby={`${rowId}-title ${rowId}-meta`}
                        aria-describedby={service.description ? `${rowId}-desc` : undefined}
                        className={cn(
                          'group flex w-full items-start gap-4 px-4 py-4 text-left transition-colors hover:bg-accent/50 sm:px-5',
                          INSET_RING,
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span id={`${rowId}-title`} className="block text-base font-semibold text-heading">
                            {service.title}
                            {titleSuffix}
                          </span>
                          {service.description && (
                            <span
                              id={`${rowId}-desc`}
                              className="mt-1 line-clamp-2 block text-sm text-muted-foreground"
                            >
                              {service.description}
                            </span>
                          )}
                          <span id={`${rowId}-meta`} className="mt-2.5 flex flex-wrap gap-2">
                            <Chip icon={Clock}>{t('service.duration', { minutes: service.durationMin })}</Chip>
                            {METHOD_LABEL[service.method] && <Chip>{t(METHOD_LABEL[service.method]!)}</Chip>}
                          </span>
                        </span>
                        <span
                          aria-hidden="true"
                          className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent text-primary-strong transition-colors group-hover:bg-primary group-hover:text-primary-foreground"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}
